import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useProductCenterApi } from 'api/ApplicationApi';
import { BaseDialog } from 'library/components/controls/dialogs/BaseDialog';
import {
	InlineDisplayTextContainer,
	InlineDisplayValue,
} from 'library/components/controls/display/InlineDisplayValue';

import { ServiceContract } from './types';

export type DeleteServiceContractDialogProps = {
	serviceContract: ServiceContract;
	onClose: () => void;
	onConfirm: () => void;
};

export const DeleteServiceContractDialog = ({
	serviceContract: { id, status },
	onClose,
	onConfirm,
}: DeleteServiceContractDialogProps) => {
	const { api } = useProductCenterApi();
	const { t } = useTranslation(['serviceContracts', 'commonButton']);
	const { enqueueSnackbar } = useSnackbar();

	const handleOnConfirm = useCallback(async () => {
		if (!id) {
			enqueueSnackbar(t('serviceContracts:delete-failure-snackbar'), {
				variant: 'error',
			});
			return;
		}

		await api.deleteServiceContract(id);
		onConfirm();
		enqueueSnackbar(t('serviceContracts:delete-success-snackbar'));
	}, [api, enqueueSnackbar, onConfirm, id, t]);

	return (
		<BaseDialog
			title={t('serviceContracts:delete-service-contract-title')}
			onClose={onClose}
			onConfirm={handleOnConfirm}
			closeOnConfirm
			confirmButtonText={t('commonButton:yes')}
			closeButtonText={t('commonButton:no')}>
			<Typography sx={{ mt: 2, mb: 4 }}>
				{t('serviceContracts:delete-service-contract-description')}
			</Typography>
			<InlineDisplayTextContainer columnsCount={1} rowsCount={2}>
				<InlineDisplayValue
					label={t('serviceContracts:service-contract')}
					value={id}
				/>
				<InlineDisplayValue
					label={t('serviceContracts:status')}
					value={status}
				/>
			</InlineDisplayTextContainer>
		</BaseDialog>
	);
};
